/**
 * Poseidon dashboard — pre-flight readiness panel (§16.3.7).
 *
 * The six checks the spec names — demo mode, bridge auth, DB, clock drift,
 * DeepSeek health, session state — plus an explicit list of blockers and whether
 * the daemon will currently accept command endpoints at all.
 */
import type { ReadinessView } from '../api/types';
import { SESSION_PHASE_LABELS, toneForReadiness } from '../lib/labels';
import { formatDateTimeWithZone } from '../lib/format';
import { Badge, KeyValueList, Panel } from './primitives';

export function ReadinessPanel({ readiness, mock = false }: { readiness: ReadinessView; mock?: boolean }): JSX.Element {
  return (
    <Panel
      title="Pre-flight readiness"
      subtitle={`overall: ${readiness.overall}`}
      mock={mock}
      actions={
        <>
          <Badge tone={toneForReadiness(readiness.overall)} large>
            {readiness.overall}
          </Badge>
          <Badge tone={readiness.commands_enabled ? 'info' : 'danger'}>
            {readiness.commands_enabled ? 'command endpoints open' : 'command endpoints closed'}
          </Badge>
        </>
      }
      footnote={
        <>
          Checked at {formatDateTimeWithZone(readiness.as_of)}. A blocking check must prevent execution from being enabled — §7.1 makes
          account mode, bridge health, database reachability and data freshness hard prerequisites, not warnings.
        </>
      }
    >
      <div className="grid" style={{ gap: 12 }}>
        {readiness.blockers.length > 0 && (
          <div className="halt-strip halt-strip--hard" style={{ alignItems: 'flex-start' }}>
            <div>
              <strong>Blocking conditions present</strong>
              <ul style={{ margin: '5px 0 0', paddingLeft: 18 }}>
                {readiness.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="grid grid--2">
          {readiness.checks.map((check) => (
            <div key={check.id} className="disclosure" style={{ borderColor: check.status === 'fail' ? '#8b2323' : undefined }}>
              <div className="data-clock">
                <Badge tone={check.status === 'pass' ? 'ok' : check.status === 'warn' ? 'warn' : check.status === 'fail' ? 'danger' : 'muted'}>
                  {check.status}
                </Badge>
                <strong>{check.label}</strong>
                {check.blocking && <Badge tone="neutral" outline>blocking</Badge>}
              </div>
              <div className="text-muted" style={{ marginTop: 5, fontSize: 12 }}>
                {check.detail}
              </div>
              {check.remediation !== null && (
                <div className="text-warn" style={{ marginTop: 5, fontSize: 12 }}>
                  Remediation: {check.remediation}
                </div>
              )}
              <div className="text-faint" style={{ fontSize: 11, marginTop: 4 }}>
                observed {formatDateTimeWithZone(check.observed_at)}
              </div>
            </div>
          ))}
        </div>

        <KeyValueList
          items={[
            { key: 'Demo mode', value: `${readiness.demo_mode.account_mode}${readiness.demo_mode.demo_verified ? ' (verified)' : ' (NOT verified)'} — ${readiness.demo_mode.detail}` },
            { key: 'Bridge auth', value: `${readiness.bridge_auth.authenticated ? 'authenticated' : 'unauthenticated'} — last heartbeat ${formatDateTimeWithZone(readiness.bridge_auth.last_heartbeat_at)}` },
            { key: 'Database', value: readiness.database.reachable ? `reachable (${readiness.database.latency_ms ?? '—'} ms)` : `NOT reachable — ${readiness.database.detail}` },
            { key: 'Clock drift', value: `${readiness.clock_drift.drift_ms ?? '—'} ms (warn ≥ ${readiness.clock_drift.warn_ms}, fail ≥ ${readiness.clock_drift.fail_ms})` },
            { key: 'DeepSeek', value: `${readiness.deepseek.health} — ${readiness.deepseek.detail}` },
            { key: 'Session', value: `${SESSION_PHASE_LABELS[readiness.session_state.phase]} — ${readiness.session_state.detail}` },
          ]}
        />
      </div>
    </Panel>
  );
}
